'use client';

import React, { useEffect, useRef } from 'react';
import { ErrorDisplay } from './components/error/ErrorDisplay';
import { useUser } from './hooks/useUser';
import { useOrderHistory } from './state/order/hooks/useOrderHistory';
import { useOrderPhoneFeeDefault } from './state/order/useOrderPhoneFeeDefault';
import { getCustomerSegmentData, getCustomerType, hashId } from './utils/utils';
import { analyticsEvents } from './utils/analytics/analytics';
import { useDeliveryPrice } from './components/shoppingCart/hooks/useDeliveryPrice';
import dayjs from 'dayjs';
import { usePathname, useRouter } from 'next/navigation';
import { usersApiClient } from './clients/users-api';
import { Capacitor } from '@capacitor/core';
import { Path } from './routes/components/path';

const APP_IS_INITIALIZED_KEY = 'AppIsInitialized';

export const AppWrapper: React.FC<React.PropsWithChildren> = ({ children }) => {
  useScrollToTop();
  useOrderPhoneFeeDefault();
  useDeliveryPrice();
  const { user, isUserLoading } = useUser();

  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || isUserLoading) return;

    // Redirect unsigned users to landing page on first app launch in this session
    if (!user && !sessionStorage.getItem(APP_IS_INITIALIZED_KEY)) {
      router.push(Path.AppLanding);
      return;
    }

    if (!user) return;

    setTimeout(() => {
      analyticsEvents.appLoginSuccessful(
        getCustomerType(user),
        getCustomerSegmentData(user),
        'refreshLogin',
        hashId(user.userId),
        user.lastPurchaseAt ? dayjs(user.lastPurchaseAt) : undefined
      );
    }, 0);

    usersApiClient.updateUserLastUsedMobileAppAt();
  }, [isUserLoading, router, user]);

  useOrderHistory();

  const appSessionNotInitialized =
    typeof window !== 'undefined' && !sessionStorage.getItem(APP_IS_INITIALIZED_KEY);

  const isNativeFirstLaunchPending =
    Capacitor.isNativePlatform() &&
    (isUserLoading || (!user && appSessionNotInitialized && pathname !== Path.AppLanding));

  return (
    <>
      <ErrorDisplay withSafeArea={true} />
      {!isNativeFirstLaunchPending && children}
    </>
  );
};

const resetScrollLock = (): void => {
  if (typeof window === 'undefined') return;

  const htmlElement = document.querySelector('html');
  if (!htmlElement) return;

  htmlElement.style.removeProperty('overflow');
  htmlElement.style.removeProperty('width');
};

const useScrollToTop = (): void => {
  const pathname = usePathname();
  const previousPath = useRef<string>('');

  useEffect(() => {
    if (typeof window === 'undefined' || pathname === previousPath.current) {
      return;
    }

    resetScrollLock();

    if (
      (pathname.includes(Path.Menu) && previousPath.current.includes(Path.Menu)) ||
      window.location.hash
    ) {
      previousPath.current = pathname;
      return;
    }

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'auto',
    });

    previousPath.current = pathname;
  }, [pathname]);
};
